import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';

interface SelectionBarProps {
  /** How many rows are ticked; the bar shows only while there is at least one. */
  count: number;
  /** Deletes the ticked rows (the screen confirms first). Left out where the rows cannot be deleted. */
  onDelete?: () => void;
  /** Exports the ticked rows to Excel. Left out where the rows have no export. */
  onExport?: () => void;
  /** Unticks every row. */
  onClear?: () => void;
  /** The screen's own actions on the ticked rows (Restore, Email ...), after Delete and Export. */
  children?: ReactNode;
}

/**
 * What a list offers once rows are ticked, the same on every list: "{n} selected", Delete selected, Export to Excel, the
 * screen's own actions, and Clear selection - as Manage &gt; Clients first had it.
 */
export function SelectionBar({ count, onDelete, onExport, onClear, children }: SelectionBarProps) {
  if (count <= 0) return null;
  return (
    <div className="selection-bar d-flex align-items-center gap-15 mb-1" role="toolbar" aria-label="Selected rows">
      <span className="fw-bold">{count} selected</span>
      {onDelete && (
        <Button className="action-button danger-button" onClick={onDelete}>
          <Icon name="delete_outline" outlined />
          <span>Delete selected</span>
        </Button>
      )}
      {onExport && (
        <Button className="action-button secondary-button" onClick={onExport}>
          <Icon name="border_all" />
          <span>Export to Excel</span>
        </Button>
      )}
      {children}
      {onClear && (
        <button type="button" className="btn-link border-0 bg-transparent text-primary p-0" onClick={onClear}>
          Clear selection
        </button>
      )}
    </div>
  );
}
